import { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

function moeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function dataBR(data) {
  if (!data) return "-";

  const texto = String(data).slice(0, 10);
  const [ano, mes, dia] = texto.split("-");

  if (ano && mes && dia) return `${dia}/${mes}/${ano}`;

  return texto;
}

function labelCategoria(categoria = "") {
  const mapa = {
    alimentacao: "Alimentação",
    combustivel: "Combustível",
    energia: "Energia",
    gas: "Gás",
    aluguel: "Aluguel",
    mercado: "Mercado",
    carro: "Carro",
    internet: "Internet",
    saude: "Saúde",
    outros: "Outros",
  };

  return mapa[categoria] || categoria;
}

function normalizarValorParaNumero(valor) {
  const limpo = String(valor || "")
    .replace("R$", "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : 0;
}

function formatarValorDigitado(valor) {
  const apenasNumeros = String(valor || "").replace(/\D/g, "");

  if (!apenasNumeros) return "";

  const numero = Number(apenasNumeros) / 100;

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function FinanceiroPessoal() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [filtro, setFiltro] = useState("mes");

  const [form, setForm] = useState({
    descricao: "",
    categoria: "alimentacao",
    valor: "",
    observacao: "",
    data: new Date().toISOString().slice(0, 10),
  });

  const token = localStorage.getItem("token");

  async function carregar() {
    try {
      setCarregando(true);
      setErro("");

      const req = await fetch(`${API}/financeiro-pessoal?filtro=${filtro}`, {
        headers: {
          Authorization: token,
        },
      });

      const texto = await req.text();

      let json = {};
      try {
        json = texto ? JSON.parse(texto) : {};
      } catch {
        throw new Error("O backend respondeu HTML. Confira se o servidor Node está rodando na porta 3001.");
      }

      if (!req.ok) {
        throw new Error(json.error || "Erro ao carregar financeiro pessoal.");
      }

      setDados(json);
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  async function salvar(e) {
    e.preventDefault();

    try {
      setSalvando(true);
      setErro("");

      const valorNumerico = normalizarValorParaNumero(form.valor);

      const req = await fetch(`${API}/financeiro-pessoal/lancamentos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          nome: form.descricao,
          descricao: form.descricao,
          categoria: form.categoria,
          tipo: "pessoal",
          valor: valorNumerico,
          observacao: form.observacao,
          data: form.data || new Date().toISOString().slice(0, 10),
        }),
      });

      const texto = await req.text();

      let json = {};
      try {
        json = texto ? JSON.parse(texto) : {};
      } catch {
        throw new Error("O backend respondeu HTML. Confira se a rota POST /financeiro-pessoal/lancamentos existe.");
      }

      if (!req.ok) {
        throw new Error(json.error || "Erro ao salvar lançamento.");
      }

      setForm({
        descricao: "",
        categoria: "alimentacao",
        valor: "",
        observacao: "",
        data: new Date().toISOString().slice(0, 10),
      });

      carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(id) {
    if (!window.confirm("Excluir lançamento pessoal?")) return;

    try {
      setErro("");

      const req = await fetch(`${API}/financeiro-pessoal/lancamentos/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: token,
        },
      });

      const texto = await req.text();
      let json = {};

      try {
        json = texto ? JSON.parse(texto) : {};
      } catch {
        throw new Error("O backend respondeu HTML ao excluir.");
      }

      if (!req.ok) {
        throw new Error(json.error || "Erro ao excluir lançamento.");
      }

      carregar();
    } catch (err) {
      setErro(err.message);
    }
  }

  const cards = dados?.cards || {};
  const lancamentos = dados?.lancamentos || [];

  const categorias = useMemo(() => {
    return [
      "alimentacao",
      "combustivel",
      "energia",
      "gas",
      "aluguel",
      "mercado",
      "carro",
      "internet",
      "saude",
      "outros",
    ];
  }, []);

  return (
    <>
      <style>{css}</style>

      <div className="financeiroPage">
        <div className="header">
          <div>
            <h1>💼 Financeiro Pessoal</h1>
            <p>
              Aqui entra somente o seu pessoal: meu lucro, despesas pessoais e saldo pessoal.
            </p>
          </div>

          <div className="filtros">
            <button className={filtro === "mes" ? "ativoFiltro" : ""} onClick={() => setFiltro("mes")}>
              Mês
            </button>

            <button className={filtro === "ultimos3meses" ? "ativoFiltro" : ""} onClick={() => setFiltro("ultimos3meses")}>
              3 meses
            </button>

            <button className={filtro === "ano" ? "ativoFiltro" : ""} onClick={() => setFiltro("ano")}>
              Ano
            </button>
          </div>
        </div>

        {erro && <div className="erro">{erro}</div>}

        {carregando ? (
          <div className="loading">Carregando...</div>
        ) : (
          <>
            <div className="cards">
              <Card
  titulo={
    filtro === "ano"
      ? "Meu lucro do ano"
      : filtro === "ultimos3meses"
      ? "Meu lucro 3 meses"
      : "Meu lucro do mês"
  }
  valor={
    cards.meuLucroMes ??
    cards.meuLucro ??
    cards.lucroReal ??
    cards.lucroDisponivel ??
    cards.lucroDisponivelEmpresa ??
    cards.lucroLiquidoEmpresa
  }
/>

              <Card
                titulo={
                  filtro === "ano"
                    ? "Despesas pessoais do ano"
                    : filtro === "ultimos3meses"
                    ? "Despesas pessoais 3 meses"
                    : "Despesas pessoais do mês"
                }
                valor={cards.despesasPessoais ?? cards.gastosPessoais}
              />

              <Card
                titulo={
                  filtro === "ano"
                    ? "Saldo pessoal do ano"
                    : filtro === "ultimos3meses"
                    ? "Saldo pessoal 3 meses"
                    : "Saldo pessoal do mês"
                }
                valor={
                  cards.saldoPessoal ??
                  cards.saldoAtual ??
                  cards.sobraPessoalReal ??
                  cards.saldoPessoalAtual
                }
                destaque
              />
            </div>

            <div className="grid">
              <div className="cardGrande">
                <h2>Nova despesa pessoal</h2>

                <form onSubmit={salvar}>
                  <input
                    placeholder="Descrição. Ex: mercado, combustível, energia"
                    value={form.descricao}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        descricao: e.target.value,
                      })
                    }
                    required
                  />

                  <select
                    value={form.categoria}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        categoria: e.target.value,
                      })
                    }
                  >
                    {categorias.map((cat) => (
                      <option key={cat} value={cat}>
                        {labelCategoria(cat)}
                      </option>
                    ))}
                  </select>

                  <input
                    placeholder="Valor. Ex: 8,50"
                    value={form.valor}
                    inputMode="numeric"
                    onChange={(e) =>
                      setForm({
                        ...form,
                        valor: formatarValorDigitado(e.target.value),
                      })
                    }
                    required
                  />

                  <input
                    type="date"
                    value={form.data}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        data: e.target.value,
                      })
                    }
                  />

                  <textarea
                    placeholder="Observação"
                    value={form.observacao}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        observacao: e.target.value,
                      })
                    }
                  />

                  <button type="submit" disabled={salvando}>
                    {salvando ? "Salvando..." : "Salvar despesa pessoal"}
                  </button>
                </form>
              </div>

              <div className="cardGrande listaCard">
                <h2>Lançamentos pessoais</h2>

                <div className="lista">
                  {lancamentos.map((item) => (
                    <div key={item._id} className="item">
                      <div>
                        <strong>{item.nome || item.descricao}</strong>
                        <p>{labelCategoria(item.categoria)}</p>
                        <small>{dataBR(item.data)}</small>
                      </div>

                      <div className="direita">
                        <strong>{moeda(item.valor)}</strong>
                        <button onClick={() => excluir(item._id)}>
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}

                  {lancamentos.length === 0 && (
                    <div className="vazio">Nenhum lançamento pessoal</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Card({ titulo, valor, destaque }) {
  return (
    <div className={destaque ? "card destaque" : "card"}>
      <p>{titulo}</p>
      <h3>{moeda(valor)}</h3>
    </div>
  );
}

const css = `
.financeiroPage {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-x: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.header h1 {
  margin: 0;
  font-size: 30px;
}

.header p {
  margin-top: 6px;
  color: #64748b;
}

.filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filtros button {
  border: none;
  background: #0f172a;
  color: white;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: bold;
}

.filtros button.ativoFiltro {
  background: #2563eb;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.card {
  background: white;
  padding: 22px;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
}

.card p {
  margin: 0;
  color: #64748b;
}

.card h3 {
  margin: 10px 0 0;
  font-size: 28px;
}

.destaque {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: white;
}

.destaque p {
  color: #cbd5e1;
}

.grid {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
}

.cardGrande {
  background: white;
  padding: 22px;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
}

.listaCard {
  min-height: 420px;
}

.cardGrande h2 {
  margin-top: 0;
}

form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

input,
textarea,
select {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
  background: white;
}

textarea {
  min-height: 100px;
}

form button {
  border: none;
  background: #0f172a;
  color: white;
  padding: 14px;
  border-radius: 12px;
  font-weight: bold;
  cursor: pointer;
}

form button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  border-radius: 14px;
  background: #f8fafc;
}

.item p {
  margin: 4px 0;
  color: #64748b;
}

.item small {
  color: #94a3b8;
}

.direita {
  text-align: right;
  display: flex;
  align-items: center;
  gap: 12px;
}

.direita button {
  border: none;
  background: #dc2626;
  color: white;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
}

.loading,
.erro,
.vazio {
  background: white;
  padding: 24px;
  border-radius: 18px;
}

.erro {
  color: red;
}

@media (max-width: 900px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .header {
    flex-direction: column;
  }

  .direita {
    flex-direction: column;
    align-items: flex-end;
  }
}
`;
